import React, { useState, Component } from 'react';
import { CheckCircle2Icon } from 'lucide-react';
import { useForm, ValidationError } from '@formspree/react';
type FormState = {
  name: string;
  email: string;
  phone: string;
  company: string;
  rut: string;
  volume: '1-5' | '6-15' | '16-30' | '31+';
};
type FormErrors = Partial<Record<keyof FormState, string>>;
// ---------- Helpers de validación & formato ----------
const isValidEmail = (email: string) => /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email.trim());
const cleanDigits = (v: string) => v.replace(/\D+/g, '');
const formatPhoneCL = (input: string) => {
  const digits = cleanDigits(input);
  let d = digits;
  if (d.startsWith('56')) d = d;else if (d.startsWith('0')) d = d.slice(1);else d = '56' + d;
  if (d.startsWith('569')) {
    // ok
  } else if (d.startsWith('56')) {
    d = '569' + d.slice(2);
  }
  const rest = d.slice(3);
  const p1 = rest.slice(0, 4);
  const p2 = rest.slice(4, 8);
  let out = '+56 9';
  if (p1) out += ' ' + p1;
  if (p2) out += ' ' + p2;
  return out;
};
const validatePhoneCL = (input: string) => /^569\d{8}$/.test(cleanDigits(input));
// RUT helpers
const cleanRut = (v: string) => v.replace(/[.\-]/g, '').toUpperCase();
const computeDv = (rutBody: string) => {
  let sum = 0,
    mul = 2;
  for (let i = rutBody.length - 1; i >= 0; i--) {
    sum += parseInt(rutBody[i], 10) * mul;
    mul = mul === 7 ? 2 : mul + 1;
  }
  const res = 11 - sum % 11;
  if (res === 11) return '0';
  if (res === 10) return 'K';
  return String(res);
};
const validateRut = (rut: string) => {
  const r = cleanRut(rut);
  if (r.length < 2) return false;
  const body = r.slice(0, -1),
    dv = r.slice(-1);
  if (!/^\d+$/.test(body)) return false;
  return computeDv(body) === dv;
};
const formatRut = (rut: string) => {
  let r = cleanRut(rut);
  if (!r) return '';
  const body = r.slice(0, -1),
    dv = r.slice(-1);
  let out = '',
    i = body.length,
    c = 0;
  while (i > 0) {
    const start = Math.max(i - 3, 0);
    const chunk = body.slice(start, i);
    out = c ? chunk + '.' + out : chunk + out;
    i = start;
    c++;
  }
  return out + '-' + dv;
};
const validateAll = (form: FormState): FormErrors => {
  const errs: FormErrors = {};
  if (!form.name.trim()) errs.name = 'Este campo es obligatorio.';
  if (!form.email.trim()) errs.email = 'Este campo es obligatorio.';else if (!isValidEmail(form.email)) errs.email = 'Ingresa un correo válido.';
  if (!form.phone.trim()) errs.phone = 'Este campo es obligatorio.';else if (!validatePhoneCL(form.phone)) errs.phone = 'Ingresa un celular chileno válido (+56 9 XXXXXXXX).';
  if (!form.rut.trim()) errs.rut = 'Este campo es obligatorio.';else if (!validateRut(form.rut)) errs.rut = 'RUT inválido. Revisa dígito verificador.';
  if (!form.volume) errs.volume = 'Selecciona una opción.';
  return errs;
};
// ---------------- Component ----------------
export function WaitlistForm() {
  const [state, handleSubmit] = useForm('meopblqb');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [form, setForm] = useState<FormState>({
    name: '',
    email: '',
    phone: '',
    company: '',
    rut: '',
    volume: '1-5'
  });
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<FormErrors>({});
  const setField = (name: keyof FormState, value: string) => setForm(f => ({
    ...f,
    [name]: value
  }));
  const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    if (name === 'phone') setField('phone', formatPhoneCL(value));else if (name === 'rut') {
      const cleaned = cleanRut(value);
      setField('rut', cleaned.length >= 2 ? formatRut(cleaned) : value.toUpperCase());
    } else setField(name as keyof FormState, value);
  };
  const onBlur = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name
    } = e.target;
    setTouched(t => ({
      ...t,
      [name]: true
    }));
    setErrors(validateAll(form));
  };
  const handleWaitlistSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const fieldErrors = validateAll(form);
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) {
      return;
    }
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('name', form.name);
      formData.append('email', form.email);
      formData.append('phone', form.phone);
      formData.append('company', form.company);
      formData.append('rut', form.rut);
      formData.append('volume', form.volume);
      formData.append('intent', 'waitlist');
      formData.append('timestamp', new Date().toISOString());
      const response = await fetch('https://formspree.io/f/meopblqb', {
        method: 'POST',
        body: formData,
        headers: {
          Accept: 'application/json'
        }
      });
      if (response.ok) {
        setShowSuccess(true);
      } else {
        console.error('Error al enviar formulario');
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  if (showSuccess) {
    return <section id="unirme" className="py-12 sm:py-20 px-4 sm:px-6 lg:px-8 bg-white" data-id="element-360">
        <div className="max-w-3xl mx-auto text-center" data-id="element-361">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 rounded-full mb-6" data-id="element-362">
            <CheckCircle2Icon className="w-8 h-8 text-emerald-600" data-id="element-363" />
          </div>
          <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4" data-id="element-364">
            ¡Listo! Quedaste en la lista de espera ✅
          </h3>
          <p className="text-lg text-gray-600 mb-6" data-id="element-365">
            Te avisaremos apenas lancemos la plataforma el{' '}
            <strong data-id="element-366">1 de enero de 2026</strong>.
          </p>
          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-6 max-w-xl mx-auto" data-id="element-367">
            <p className="text-indigo-900 font-medium mb-2" data-id="element-368">
              Serás parte de nuestra <strong data-id="element-369">red privada exclusiva</strong>{' '}
              desde el primer día
            </p>
            <p className="text-sm text-indigo-800" data-id="element-370">
              Publica propiedades gratis y recibe{' '}
              <strong data-id="element-371">3 créditos mensuales</strong> para solicitar canjes
            </p>
          </div>
        </div>
      </section>;
  }
  return <section id="unirme" className="py-12 sm:py-20 px-4 sm:px-6 lg:px-8 bg-white" data-id="element-372">
      <div className="max-w-4xl mx-auto" data-id="element-373">
        <div className="text-center mb-8 sm:mb-10" data-id="element-374">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4" data-id="element-375">
            Inscríbete en la lista de espera
          </h2>
          <p className="text-base sm:text-lg text-gray-600" data-id="element-376">
            Sé de los primeros en acceder a nuestra red privada. Lanzamiento el
            1 de enero de 2026.
          </p>
        </div>
        <div className="bg-indigo-50 border border-indigo-200 rounded-2xl p-5 sm:p-6 mb-6 text-center" data-id="element-377">
          <p className="text-base sm:text-lg text-indigo-900 font-semibold mb-2" data-id="element-378">
            Únete a la red privada de corredores más exclusiva
          </p>
          <p className="text-sm sm:text-base text-indigo-800" data-id="element-379">
            Publica propiedades gratis y recibe{' '}
            <strong data-id="element-380">3 créditos mensuales</strong> para solicitar canjes desde el
            día uno
          </p>
        </div>
        <form onSubmit={handleWaitlistSubmit} className="bg-gray-50 rounded-2xl p-6 sm:p-8 border border-gray-200" noValidate data-id="element-381">
          <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 mb-6" data-id="element-382">
            <div data-id="element-383">
              <label className="block text-sm font-semibold text-gray-900 mb-2" data-id="element-384">
                Nombre y Apellido *
              </label>
              <input name="name" required value={form.name} onChange={onChange} onBlur={onBlur} aria-invalid={!!(touched.name && errors.name)} className={`w-full px-4 py-3 rounded-xl border ${touched.name && errors.name ? 'border-red-500' : 'border-gray-300'} focus:border-indigo-900 focus:ring-2 focus:ring-indigo-900/20 transition-all`} placeholder="Ej: María González" autoComplete="name" data-id="element-385" />
              {touched.name && errors.name && <p className="text-red-600 text-xs mt-1" data-id="element-386">{errors.name}</p>}
            </div>
            <div data-id="element-387">
              <label className="block text-sm font-semibold text-gray-900 mb-2" data-id="element-388">
                Correo electrónico *
              </label>
              <input name="email" type="email" required value={form.email} onChange={onChange} onBlur={onBlur} aria-invalid={!!(touched.email && errors.email)} className={`w-full px-4 py-3 rounded-xl border ${touched.email && errors.email ? 'border-red-500' : 'border-gray-300'} focus:border-indigo-900 focus:ring-2 focus:ring-indigo-900/20 transition-all`} placeholder="correo@empresa.cl" autoComplete="email" inputMode="email" data-id="element-389" />
              {touched.email && errors.email && <p className="text-red-600 text-xs mt-1" data-id="element-390">{errors.email}</p>}
            </div>
            <div data-id="element-391">
              <label className="block text-sm font-semibold text-gray-900 mb-2" data-id="element-392">
                Teléfono *
              </label>
              <input name="phone" required value={form.phone} onChange={onChange} onBlur={onBlur} aria-invalid={!!(touched.phone && errors.phone)} className={`w-full px-4 py-3 rounded-xl border ${touched.phone && errors.phone ? 'border-red-500' : 'border-gray-300'} focus:border-indigo-900 focus:ring-2 focus:ring-indigo-900/20 transition-all`} placeholder="+56 9 1234 5678" inputMode="tel" autoComplete="tel" data-id="element-393" />
              {touched.phone && errors.phone && <p className="text-red-600 text-xs mt-1" data-id="element-394">{errors.phone}</p>}
            </div>
            <div data-id="element-395">
              <label className="block text-sm font-semibold text-gray-900 mb-2" data-id="element-396">
                Razón social (opcional)
              </label>
              <input name="company" value={form.company} onChange={onChange} onBlur={onBlur} className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-indigo-900 focus:ring-2 focus:ring-indigo-900/20 transition-all" placeholder="Mi Corredora SpA" autoComplete="organization" data-id="element-397" />
            </div>
            <div data-id="element-398">
              <label className="block text-sm font-semibold text-gray-900 mb-2" data-id="element-399">
                RUT *
              </label>
              <input name="rut" required value={form.rut} onChange={onChange} onBlur={onBlur} aria-invalid={!!(touched.rut && errors.rut)} className={`w-full px-4 py-3 rounded-xl border ${touched.rut && errors.rut ? 'border-red-500' : 'border-gray-300'} focus:border-indigo-900 focus:ring-2 focus:ring-indigo-900/20 transition-all`} placeholder="12.345.678-9" inputMode="text" data-id="element-400" />
              {touched.rut && errors.rut && <p className="text-red-600 text-xs mt-1" data-id="element-401">{errors.rut}</p>}
            </div>
            <div data-id="element-402">
              <label className="block text-sm font-semibold text-gray-900 mb-2" data-id="element-403">
                Nº de propiedades *
              </label>
              <select name="volume" required value={form.volume} onChange={onChange} onBlur={onBlur} aria-invalid={!!(touched.volume && errors.volume)} className={`w-full px-4 py-3 rounded-xl border ${touched.volume && errors.volume ? 'border-red-500' : 'border-gray-300'} focus:border-indigo-900 focus:ring-2 focus:ring-indigo-900/20 transition-all`} data-id="element-404">
                <option value="1-5" data-id="element-405">1–5 propiedades</option>
                <option value="6-15" data-id="element-406">6–15 propiedades</option>
                <option value="16-30" data-id="element-407">16–30 propiedades</option>
                <option value="31+" data-id="element-408">31+ propiedades</option>
              </select>
              {touched.volume && errors.volume && <p className="text-red-600 text-xs mt-1" data-id="element-409">{errors.volume}</p>}
            </div>
          </div>
          <button type="submit" disabled={isSubmitting} className="w-full px-6 py-4 rounded-xl font-semibold text-white bg-indigo-900 hover:bg-indigo-800 transition-all disabled:opacity-70 hover:shadow-lg hover:-translate-y-0.5" aria-label="Quiero mi descuento del 50%" data-id="element-410">
            {isSubmitting ? 'Guardando…' : 'Quiero mi descuento del 50%'}
          </button>
          <p className="text-xs text-gray-500 text-center mt-4" data-id="element-411">
            Te avisaremos apenas lancemos la plataforma y podrás activar tu
            cuenta en nuestra red privada.
          </p>
        </form>
      </div>
    </section>;
}